import store from "../../../../../../../../store";
import {setWizardSelectedValues} from "../../../../../../../../actions/wizardFunctions";
import {getCurrentLayout} from "../../../../../../../appLayout";
import {
  SQFT_PARENT_FEATURE_GROUP_PART_IDS,
  SHAPE_PART_ID,
} from "../../../../../../constants";
import {
  getAvailableSteps,
  getCurrentWizardNavItems,
  getFeatureGroupFromWizardGroup,
} from "../../../../../../../look/lookNavigation/lookMain/lookWizard/lookWizardFunctions";
import {
  selectedWizardGroupIsNotInCurrentStepAnymore
} from "../../../../../../../look/lookNavigation/lookMain/lookWizard/lookWizardFunctions2";
import {
  BOTTOM_CABINET_GROUP_NAME,
  getCurrentBottomCabinetWizardGroup,
  getEquivalentBottomCabinetFeature,
  getLastWizardSelectedFeatureByPartId,
  getMatchingBottomWizardNavItem,
} from "./bottomCabinetWizardMatcher";
import {applyWizardPropsToFeature} from "../../../../../../helpers/wizardFeaturePropsHelper";

const MAX_WIZARD_STEPS_TO_RESOLVE = 50;

/**
 * Bottom cabinet SQFT drop flow:
 * getResolvedCabinetParentFeature
 *   -> getMatchingBottomCabinetFeatureFromTop
 *   -> activateFeatureInWizard
 *   -> buildWizardValuesThroughFeature
 *
 * The matcher module owns SKU/profile/color matching. This file owns when we
 * replay wizard state and when we apply the resolved feature to all walls.
 */

const getActiveTopCabinetFeature = (layout) => {
  const topCabinetFeatureGroup = layout.getFeatureGroupByPartId(SHAPE_PART_ID.top_cabinet);
  return topCabinetFeatureGroup?.activeFeature;
};

const getAutoSkippedWizardValue = (wizardGroup) => ({
  wizardGroup,
  isAutoSkippedStep: true,
});

const getSelectedWizardFeatureValue = (wizardGroup, navItem, navItems) => ({
  wizardGroup,
  selectedFeature: navItem.part,
  partId: navItem.part.partId,
  singleFeature: navItems.length === 1,
});

const getWizardPropsFromSelectedValues = (wizardValues, wizardItem, partId) => {
  const selectedStepIndex = Object.keys(wizardValues)
    .map(stepIndex => parseInt(stepIndex, 10))
    .filter(stepIndex => {
      return Number.isInteger(stepIndex)
        && wizardValues[stepIndex]?.partId === partId
        && wizardValues[stepIndex]?.selectedFeature;
    })
    .sort((a, b) => b - a)[0];

  if (!Number.isInteger(selectedStepIndex)) {
    return null;
  }

  const selectedWizardValue = wizardValues[selectedStepIndex];

  return {
    fromWizardId: wizardItem?.id,
    fromWizardStep: selectedStepIndex,
    fromWizardGroupId: selectedWizardValue.wizardGroup?.id,
  };
};

/**
 * Replays only the wizard path needed to select Bottom Cabinet.
 *
 * Business rule: SQFT can add a concrete lower cabinet SKU while the Renuit
 * wizard may still be before lower cabinets. We auto-skip only the required
 * previous steps, select matching Bottom Cabinet profile/color, then stop so
 * unrelated later wizard groups are not touched.
 */
const buildWizardValuesThroughFeature = (layout, feature) => {
  const {wizardState} = store.getState();

  if (!wizardState.wizardIsActive || !wizardState.wizardActiveItem || !feature) {
    return null;
  }

  const wizardItem = wizardState.wizardActiveItem;
  const selectedValues = Object.assign({}, wizardState.wizardSelectedValues);
  let hasSelectedBottomCabinetStep = false;

  for (let stepIndex = 0; stepIndex < MAX_WIZARD_STEPS_TO_RESOLVE; stepIndex++) {
    const wizardSteps = getAvailableSteps(wizardItem, selectedValues, true);
    const wizardStep = wizardSteps[stepIndex];
    let wizardGroup = wizardStep?.stepGroups?.[0];

    if (!wizardGroup) {
      break;
    }

    if (wizardGroup.groupName !== BOTTOM_CABINET_GROUP_NAME) {
      if (hasSelectedBottomCabinetStep) {
        return selectedValues;
      }

      if (!selectedValues[stepIndex]
        || selectedWizardGroupIsNotInCurrentStepAnymore(selectedValues[stepIndex], wizardStep.stepGroups)) {
        selectedValues[stepIndex] = getAutoSkippedWizardValue(wizardGroup);
      }
      continue;
    }

    wizardGroup = getCurrentBottomCabinetWizardGroup(wizardStep, feature);

    const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup, layout);
    const navItems = getCurrentWizardNavItems(
      selectedValues,
      stepIndex,
      wizardItem,
      wizardGroup,
      featureGroup
    );
    const matchingNavItem = getMatchingBottomWizardNavItem(layout, navItems, wizardGroup, feature, selectedValues);

    if (matchingNavItem?.part) {
      selectedValues[stepIndex] = getSelectedWizardFeatureValue(wizardGroup, matchingNavItem, navItems);
      hasSelectedBottomCabinetStep = true;
    } else if (!selectedValues[stepIndex]) {
      selectedValues[stepIndex] = getAutoSkippedWizardValue(wizardGroup);
    }
  }

  return hasSelectedBottomCabinetStep ? selectedValues : null;
};

/**
 * Applies the resolved Bottom Cabinet feature both to wizardSelectedValues and
 * to the visualizer feature state. Wizard state is dispatched first because
 * clickFromAllWalls can synchronously re-enter resolver-driven logic.
 */
const activateFeatureInWizard = (layout, feature) => {
  const wizardValues = buildWizardValuesThroughFeature(layout, feature);
  if (wizardValues) {
    applyWizardPropsToFeature(
      feature,
      getWizardPropsFromSelectedValues(
        wizardValues,
        store.getState().wizardState.wizardActiveItem,
        SHAPE_PART_ID.bottom_cabinet
      )
    );

    store.dispatch(setWizardSelectedValues(
      wizardValues,
      true,
      store.getState().wizardState.wizardActiveItem
    ));
  }

  if (typeof feature.clickFromAllWalls === "function") {
    feature.clickFromAllWalls(true);
  }

  return feature;
};

/**
 * When Bottom Cabinet has not been explicitly selected, inherit it from the
 * active Top Cabinet. This keeps lower cabinet boxes aligned with the selected
 * cabinet style/color instead of using a stale bottom activeFeature.
 */
const getMatchingBottomCabinetFeatureFromTop = (layout) => {
  const bottomCabinetFeatureGroup = layout.getFeatureGroupByPartId(SHAPE_PART_ID.bottom_cabinet);
  const topCabinetFeature = getActiveTopCabinetFeature(layout);

  if (!bottomCabinetFeatureGroup?.features || !topCabinetFeature) {
    return null;
  }

  const matchingBottomCabinetFeature = getEquivalentBottomCabinetFeature(layout, topCabinetFeature);

  return matchingBottomCabinetFeature
    ? activateFeatureInWizard(layout, matchingBottomCabinetFeature)
    : null;
};

/**
 * Resolves the wizard parent feature used by SQFT cabinet-box shapes.
 *
 * Lower cabinet boxes normally depend on the Bottom Cabinet wizard feature, but
 * that step can be skipped when it is not required. In that case the lower box
 * should still use the Bottom Cabinet feature that matches the selected Top
 * Cabinet style/color, instead of falling back to a stale or default Bottom
 * Cabinet activeFeature. If Bottom Cabinet was explicitly selected in the
 * wizard, the last selected wizard feature wins because split profile/color
 * steps can share the same partId.
 */
export const getResolvedCabinetParentFeature = ({
  feature,
  parentFeature,
  partId,
  wizardSelectedValues = store.getState().wizardState.wizardSelectedValues,
  layout = getCurrentLayout(),
}) => {
  const currentParentPartId = partId
    || SQFT_PARENT_FEATURE_GROUP_PART_IDS[feature?.partId]
    || parentFeature?.partId;

  if (currentParentPartId !== SHAPE_PART_ID.bottom_cabinet) {
    return parentFeature;
  }

  const selectedBottomCabinetFeature = getLastWizardSelectedFeatureByPartId(
    wizardSelectedValues,
    SHAPE_PART_ID.bottom_cabinet
  );

  if (selectedBottomCabinetFeature) {
    return selectedBottomCabinetFeature;
  }

  return getMatchingBottomCabinetFeatureFromTop(layout) || parentFeature;
};
